import React from "react";
import { DOC_CENTER } from "@/constants/testIds";
import { Layers, FileSearch, ShoppingCart, FolderKanban, Truck, ClipboardCheck, Radar, PackageCheck } from "lucide-react";

const LAYERS = [
  { slug: "rfq", label: "RFQ™", caption: "Source", icon: FileSearch },
  { slug: "commoditybid", label: "CommodityBid™", caption: "Negotiate", icon: ShoppingCart },
  { slug: "purchase-order", label: "Purchase Order", caption: "Commit", icon: ClipboardCheck },
  { slug: "document-center", label: "Document Center™", caption: "The connected documentation layer", icon: Layers, central: true },
  { slug: "freightiq", label: "FreightIQ™", caption: "Move", icon: Truck },
  { slug: "inspection", label: "Inspection™", caption: "Verify", icon: FolderKanban },
  { slug: "live-tracking", label: "Live Shipment Tracking™", caption: "Track", icon: Radar },
  { slug: "delivered", label: "Delivered", caption: "Complete", icon: PackageCheck },
];

export default function WorkspaceIntegration() {
  return (
    <section
      id="workspace"
      data-testid={DOC_CENTER.workspace.section}
      className="relative overflow-hidden bg-[#0A0A0A] text-white"
    >
      <div className="pointer-events-none absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(255,255,255,0.6) 1px, transparent 1px),linear-gradient(to bottom, rgba(255,255,255,0.6) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
        }} />
      <div className="dc-container relative py-28 md:py-36">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <span className="dc-eyebrow !text-neutral-400">Workspace integration</span>
            <h2 className="mt-4 font-display text-4xl font-black leading-[1.05] tracking-tighter text-white sm:text-5xl md:text-[56px]">
              The documentation layer of your<br />import operating system.
            </h2>
            <p className="mt-5 max-w-md text-[15px] leading-relaxed text-neutral-400">
              Document Center™ sits at the centre of every DeMaxtore Workspace product. Every operation
              flows through it, and every document attaches to the operation it serves.
            </p>

            <div className="mt-8 grid grid-cols-2 gap-3 max-w-md">
              <Stat number="01" label="Source of truth" />
              <Stat number="08" label="Connected products" />
              <Stat number="∞" label="Versioned documents" />
              <Stat number="0" label="Folders you’ll create" />
            </div>
          </div>

          {/* Layer stack */}
          <div className="lg:col-span-7">
            <div className="relative rounded-3xl border border-white/10 bg-white/[0.02] p-5 sm:p-8">
              <div className="absolute -left-2 top-6 hidden flex-col gap-2 font-mono text-[10px] uppercase tracking-wider text-neutral-500 sm:flex">
                <span>layer 01</span>
                <span className="mt-[44px]">layer 02</span>
                <span className="mt-[44px]">layer 03</span>
                <span className="mt-[44px]">layer 04</span>
              </div>

              <div className="space-y-2.5">
                {LAYERS.map((l, i) => {
                  const Icon = l.icon;
                  return (
                    <div
                      key={l.slug}
                      data-testid={DOC_CENTER.workspace.layer(l.slug)}
                      className={`group flex items-center justify-between gap-4 rounded-xl border px-4 py-3.5 transition-all duration-200 ${
                        l.central
                          ? "border-[color:var(--dc-accent)]/50 bg-gradient-to-r from-[#0b1d3f] via-[#0e2a5c] to-[#0b1d3f] shadow-[0_0_0_1px_rgba(37,99,235,0.18),0_30px_60px_-25px_rgba(37,99,235,0.55)]"
                          : "border-white/10 bg-white/[0.03] hover:bg-white/[0.06]"
                      }`}
                      style={{ animationDelay: `${i * 60}ms` }}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`grid h-10 w-10 place-items-center rounded-lg border ${
                            l.central
                              ? "border-[color:var(--dc-accent)]/40 bg-[color:var(--dc-accent)]/15 text-blue-200"
                              : "border-white/10 bg-black/30 text-neutral-200"
                          }`}
                        >
                          <Icon className="h-5 w-5" strokeWidth={1.5} />
                        </div>
                        <div>
                          <div className={`text-[14.5px] font-semibold ${l.central ? "text-white" : "text-neutral-200"}`}>{l.label}</div>
                          <div className="mt-0.5 font-mono text-[10.5px] uppercase tracking-wider text-neutral-500">{l.caption}</div>
                        </div>
                      </div>
                      <span className={`font-mono text-[10.5px] uppercase tracking-wider ${l.central ? "text-blue-300" : "text-neutral-500"}`}>
                        {l.central ? "central layer" : `0${i + 1}`}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Stat({ number, label }) {
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3">
      <div className="font-mono text-[11px] uppercase tracking-wider text-neutral-500">{label}</div>
      <div className="mt-1 font-display text-2xl font-black tracking-tight text-white">{number}</div>
    </div>
  );
}
